import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import { PlatformTypes } from '../enums';
import type { BinaryReader } from '../BinaryReader';

/**
 * Login / handshake codec. Mirrors the server's RegisterModule.cs exactly.
 *
 * Handshake order over the WebSocket (each is one binary frame `[ushort tag][payload]`):
 *   1. LoadLogin  (client -> gateway)  compatibility value; gateway rewrites it to TG:<verified id>
 *   2. Login      (client -> server)  username/password/platform/version
 *   3. Login      (server -> client)  success packet incl. balance + reconnect token
 *      ...or SendMessage (server -> client) a rejection popup string on failure.
 */

/** Version string accepted by the unchanged production game server. */
export const GAME_VERSION = '2.9.21';

/**
 * LoadLogin (tag 1): client announces its hardware id BEFORE Login.
 * Server reads one string and binds it (RegisterModule.cs:155-156, netPeer.SetMacAddress). Browser
 * input is untrusted: the gateway discards this field and writes TG:<verified Telegram user ID>.
 */
export function encodeLoadLogin(macAddress: string): Uint8Array {
  return beginMessage(MessageTag.LoadLogin).putString(macAddress).toUint8Array();
}

export interface LoadLoginResult {
  username: string;
  shouldLockUsername: boolean;
}

/**
 * Decodes the optional LoadLogin response sent when the verified identity is already bound.
 * RegisterModule writes the bound username followed by the read-only flag.
 */
export function decodeLoadLoginResult(reader: BinaryReader): LoadLoginResult {
  return {
    username: reader.getString(),
    shouldLockUsername: reader.getBool(),
  };
}

export interface LoginRequest {
  username: string;
  password: string;
  /** Defaults to the canonical Web platform (3). */
  platform?: PlatformTypes;
  /** Defaults to GAME_VERSION ("2.9.21"); must match Settings.GameVersion or login diverts to Update. */
  version?: string;
}

/**
 * Login (tag 2): RegisterModule.cs:79-82 reads, strictly in this order:
 *   string username, string password, short platform, string version.
 */
export function encodeLogin(req: LoginRequest): Uint8Array {
  return beginMessage(MessageTag.Login)
    .putString(req.username)
    .putString(req.password)
    .putShort(req.platform ?? PlatformTypes.Web)
    .putString(req.version ?? GAME_VERSION)
    .toUint8Array();
}

export interface PlayerModel {
  userId: bigint;
  username: string;
  avatar: number;
}

export interface LoginResult {
  ok: boolean;
  player: PlayerModel;
  /** Account balance in the smallest currency unit; 64-bit on the wire, hence bigint. */
  balance: bigint;
  onlineCount: number;
  reconnectToken: string;
}

/**
 * Decodes a successful Login response (tag 2). RegisterModule.cs:139-147 writes, in order:
 *   bool ok(=true),
 *   PlayerModel { long userId, string username, int avatar }   (CommonNetworkModels.cs:230-235),
 *   long balance, int onlineCount, string reconnectToken.
 * Login *failures* never use this tag — they arrive as MessageTag.SendMessage (decodeServerMessage).
 */
export function decodeLoginResult(reader: BinaryReader): LoginResult {
  const ok = reader.getBool();
  const player: PlayerModel = {
    userId: reader.getLong(),
    username: reader.getString(),
    avatar: reader.getInt(),
  };
  const balance = reader.getLong();
  const onlineCount = reader.getInt();
  const reconnectToken = reader.getString();
  return { ok, player, balance, onlineCount, reconnectToken };
}

/**
 * Decodes a MessageTag.SendMessage (tag 0) payload — a single string. The server uses this for
 * direct popups, including login rejections
 * (PlayerService.SendDirectMessage -> CreatePacketWithMessage = packet.Put(message)).
 */
export function decodeServerMessage(reader: BinaryReader): string {
  return reader.getString();
}
